/** tm/components/GovRecoList.tsx — 업체 상세 「추천 정부지원사업」 (2026-10-07 지도사 「공고문 보기 · 링크 복사 · 모집기간도 나와야지 → 월별 · 지원금 종류별로 선택」)
 *
 *  판정은 서버 엔진(GET /companies/:id/gov-matches · services/govMatch 기본 모드) 그대로 — 해당(pass) · 확인 필요(near = 업체 칸이 비어 판정 못 함)
 *  모집기간 한 줄 · 월 겹침은 govPeriod.ts 한 벌(GovPage 추천 문자와 같은 규칙) — 마감 없음 = 상시 · 선착순도 아니면 「공고문 확인」
 *  종류 칩은 GovTaxonomy 대분류(제품개발 · 생산 · 고용 · 마케팅 · 기타 · 정책자금) — 업체 「관심 분류」 칩(CompanyMatchFields → GovCategoryPicker)과 같은 이름
 *  엔진은 날짜를 보지 않고 마감 순으로 돌려줘 지난 공고가 맨 앞에 오던 것 — 지난 공고는 접어 둔다(「지난 공고 n건」) · 매년 반복 공고는 내년 같은 날짜로 본다
 *  필터가 없으면 앞 limit 건 + 「n건 더 보기」, 필터를 고르면 맞는 공고 전부(스크롤 영역) — 업체 상세 3컬럼 · 모바일 한 컬럼 모두 (메타 한 줄 + 단추 묶음은 좁으면 줄바꿈)
 *  업체 상세(CompanyDetailPanel) 말고 추천 카드가 또 생기면 이 컴포넌트를 그대로 쓴다
 */
import React, { useEffect, useMemo, useState } from 'react';
import * as api from '../api';
import type { GovCompanyMatch, GovCompanyMatches } from '../api';
import { C, T, R, DDayBadge, Badge } from './ui';
import { GOV_TAXONOMY, canonCat, groupOf } from './GovTaxonomy';
import { govPeriod, monthSlots, periodInMonth, type GovPeriod, type MonthSlot } from './govPeriod';

/** 노란 상자 색 — 업체 상세의 「추천 정부지원사업」 블록이 쓰던 그대로 */
const Y = { bg: '#FEFCE8', bd: '#FDE68A', fg: '#92400E', on: '#FEF3C7', onBd: '#F59E0B' } as const;
const MAX_H = 380;   // 필터 · 펼침 때 목록 높이 — 상세 패널이 한없이 길어지지 않게

interface Props {
  companyId: string;
  /** 업체 매칭 칸이 바뀌면(저장 → store 새로고침 → updated_at) 다시 판정한다 */
  refreshKey?: string | null;
  onNavigateGov?: () => void;
  onToast: (msg: string, type?: 'success' | 'error') => void;
  /** 필터 없을 때 기본으로 보이는 건수 */
  limit?: number;
  /** 「다음 공고 알림」 — 이 사업 공고가 새로 나오면 알려 주기(업무 탭 「기다리는 공고」 입력을 그 사업으로 연다 · 2026-10-30) */
  onWatch?: (p: GovCompanyMatch) => void;
}

interface Row { p: GovCompanyMatch; near: boolean; pr: GovPeriod; kinds: string[] }

/** 공고 분류 칸(쉼표) → GovTaxonomy 대분류 k 목록 (기업 유형 같은 체계 밖 라벨은 뺀다) */
function kindsOf(category: string | null | undefined): string[] {
  const out: string[] = [];
  String(category || '').split(',').map(s => s.trim()).filter(Boolean).forEach(c => {
    const g = groupOf(canonCat(c));
    if (g && !out.includes(g.k)) out.push(g.k);
  });
  return out;
}
/** 링크 — 짧은 주소 먼저 · 스킴 없으면 https */
function urlOf(p: GovCompanyMatch): string {
  const u = String(p.short_url || p.link || '').trim();
  if (!u) return '';
  return /^https?:\/\//i.test(u) ? u : `https://${u}`;
}
/** 클립보드 — 보안 컨텍스트가 아니거나(http) 막히면 숨은 textarea 로 */
async function copyText(text: string): Promise<boolean> {
  try { if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); return true; } } catch { /* 아래 fallback */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.top = '0'; ta.style.left = '0'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select(); ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

const chip = (on: boolean, dim = false): React.CSSProperties => ({
  padding: '2px 8px', borderRadius: 999, border: `1px solid ${on ? Y.onBd : Y.bd}`, background: on ? Y.on : C.white,
  color: on ? Y.fg : dim ? C.muted : C.sub, fontSize: T.xs, fontWeight: on ? 700 : 500, cursor: 'pointer', fontFamily: 'inherit', lineHeight: '16px', whiteSpace: 'nowrap',
});
const smallBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 3, height: 22, padding: '0 8px', borderRadius: R.sm, border: `1px solid ${Y.bd}`, background: C.white,
  color: Y.fg, fontSize: T.xs, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'none', whiteSpace: 'nowrap', boxSizing: 'border-box',
};
const rowLbl: React.CSSProperties = { fontSize: T.xs, fontWeight: 600, color: C.muted, width: 30, flexShrink: 0, lineHeight: '20px' };

export default function GovRecoList({ companyId, refreshKey, onNavigateGov, onToast, limit = 5, onWatch }: Props) {
  const [data, setData] = useState<GovCompanyMatches | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [tick, setTick] = useState(0);                          // 「다시 시도」
  const [month, setMonth] = useState<string | null>(null);      // 'YYYY-MM' · null = 전체
  const [kinds, setKinds] = useState<string[]>([]);             // 대분류 k (복수) · 비면 전체
  const [showNear, setShowNear] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr('');
    api.getCompanyGovMatches(companyId)
      .then(r => { if (cancelled) return; if (!r.success || !r.data) throw new Error(r.error || '추천을 불러오지 못했습니다'); setData(r.data); })
      .catch((e: unknown) => { if (!cancelled) { setData(null); setErr(e instanceof Error ? e.message : '추천을 불러오지 못했습니다'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyId, refreshKey, tick]);
  // 업체가 바뀌면 고른 것 초기화 — 다른 업체에 전 업체의 달 · 종류가 남지 않게
  useEffect(() => { setMonth(null); setKinds([]); setShowNear(false); setShowPast(false); setExpanded(false); }, [companyId]);

  const months = useMemo<MonthSlot[]>(() => monthSlots(12), []);
  const rows = useMemo<Row[]>(() => {
    if (!data) return [];
    const mk = (p: GovCompanyMatch, near: boolean): Row => ({ p, near, pr: govPeriod(p), kinds: kindsOf(p.category) });
    // 마감 가까운 순(상시 · 미정은 뒤 · 지난 공고는 맨 뒤) · 해당 먼저 — 매년 반복은 내년 날짜로 줄을 선다
    const byEnd = (a: Row, b: Row) => (Number(a.pr.expired) - Number(b.pr.expired)) || (a.pr.end || '9999').localeCompare(b.pr.end || '9999');
    return [...data.pass.map(p => mk(p, false)).sort(byEnd), ...data.near.map(p => mk(p, true)).sort(byEnd)];
  }, [data]);

  const slot = month ? months.find(m => m.key === month) || null : null;
  const filtering = !!slot || kinds.length > 0;
  const hit = useMemo(() => rows.filter(r => (!slot || periodInMonth(r.pr, slot)) && (!kinds.length || r.kinds.some(k => kinds.includes(k)))), [rows, slot, kinds]);
  const live = hit.filter(r => !r.pr.expired);
  const past = hit.filter(r => r.pr.expired);
  const nPass = live.filter(r => !r.near).length, nNear = live.filter(r => r.near).length;
  // 보이는 줄 — 지난 공고 · 확인 필요는 토글로, 필터 · 펼침이 없으면 앞 limit 건만
  const base = (showPast ? hit : live).filter(r => showNear || !r.near);
  const shown = filtering || expanded ? base : base.slice(0, limit);
  const hidden = base.length - shown.length;

  const toggleKind = (k: string) => setKinds(ks => (ks.includes(k) ? ks.filter(x => x !== k) : [...ks, k]));
  async function copy(p: GovCompanyMatch) {
    const u = urlOf(p);
    if (!u) return;
    const ok = await copyText(u);
    onToast(ok ? '링크를 복사했습니다' : '복사하지 못했습니다 — 주소를 직접 복사해 주세요', ok ? 'success' : 'error');
  }

  const total = rows.length;
  return (
    <div style={{ marginBottom: 12, padding: 10, borderRadius: R.md, background: Y.bg, border: `1px solid ${Y.bd}` }}>
      {/* 머리 — 제목 · 건수 · 확인 필요 · 지난 공고 토글 */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        <span style={{ fontSize: T.xs, fontWeight: 700, color: Y.fg }}>추천 정부지원사업</span>
        {!loading && !err && total > 0 && (
          <span style={{ fontSize: T.xs, color: Y.fg, opacity: 0.8 }}>해당 {nPass}건 · 확인 필요 {nNear}건{filtering ? ' (고른 조건)' : ''}</span>
        )}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {nNear > 0 && (
            <button type="button" onClick={() => setShowNear(v => !v)} title="업체 칸(지역 · 업종 · 규모 …)이 비어 판정하지 못한 공고 — 채우면 해당 여부가 정해집니다" style={chip(showNear)}>
              확인 필요 {showNear ? '감추기' : '보기'}
            </button>
          )}
          {past.length > 0 && (
            <button type="button" onClick={() => setShowPast(v => !v)} title="마감이 지난 공고 — 매년 반복 공고는 내년 같은 날짜로 보여 여기 들지 않습니다" style={chip(showPast, true)}>
              지난 공고 {past.length}건
            </button>
          )}
        </span>
      </div>

      {loading && <div style={{ fontSize: T.xs, color: Y.fg, opacity: 0.7, padding: '4px 0' }}>매칭하는 중…</div>}
      {!loading && err && (
        <div style={{ fontSize: T.xs, color: C.red, padding: '4px 0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>{err}</span>
          <button type="button" onClick={() => setTick(t => t + 1)} style={smallBtn}>다시 시도</button>
        </div>
      )}
      {!loading && !err && total === 0 && <div style={{ fontSize: T.xs, color: Y.fg, opacity: 0.7, padding: '4px 0' }}>분류를 설정하면 매칭되는 정부지원사업이 표시됩니다</div>}

      {!loading && !err && total > 0 && (
        <>
          {/* 월별 — 이번 달부터 12개월 · 모집기간이 그 달과 겹치는 공고 (상시는 모든 달) */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 5 }}>
            <span style={rowLbl}>월별</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
              <button type="button" onClick={() => setMonth(null)} style={chip(!slot)}>전체</button>
              {months.map((m, i) => (
                <React.Fragment key={m.key}>
                  {i > 0 && m.m === 1 && <span style={{ fontSize: T.xs, color: C.muted, padding: '0 2px' }}>{m.y}</span>}
                  <button type="button" onClick={() => setMonth(month === m.key ? null : m.key)} title={`${m.y}년 ${m.m}월에 모집하는 공고`} style={chip(month === m.key)}>{m.m}월</button>
                </React.Fragment>
              ))}
            </div>
          </div>
          {/* 종류별 — 대분류(복수) · 공고 분류 칸이 그 대분류(또는 세부)에 있으면 */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 8 }}>
            <span style={rowLbl}>종류</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              <button type="button" onClick={() => setKinds([])} style={chip(kinds.length === 0)}>전체</button>
              {GOV_TAXONOMY.map(g => (
                <button key={g.k} type="button" onClick={() => toggleKind(g.k)} title={g.items.join(' · ')} style={chip(kinds.includes(g.k))}>
                  {g.noGroupLabel ? `${g.name}(${g.items.join('·')})` : g.name}
                </button>
              ))}
            </div>
          </div>

          {/* 목록 — 필터 · 펼침이면 전부(스크롤) · 아니면 앞 limit 건 */}
          {shown.length === 0 && (
            <div style={{ fontSize: T.xs, color: Y.fg, opacity: 0.7, padding: '4px 0' }}>
              {filtering ? '이 조건에 맞는 공고가 없습니다' : nNear > 0 ? '해당 공고는 없고 확인 필요만 있습니다 — 「확인 필요 보기」를 눌러 보세요' : '지금 모집 중인 공고가 없습니다'}
            </div>
          )}
          <div style={filtering || expanded ? { maxHeight: MAX_H, overflowY: 'auto', paddingRight: 2 } : undefined}>
            {shown.map(r => {
              const p = r.p, u = urlOf(p);
              const whyOk = (p.why || []).filter(w => w.k === 'ok').map(w => w.t);
              const meta = [p.agency || null, String(p.amount || '').trim() || null].filter(Boolean) as string[];
              return (
                <div key={p.id} title={whyOk.length ? `맞은 이유: ${whyOk.join(' · ')}` : undefined}
                  style={{ padding: '7px 10px', borderRadius: R.sm, background: C.white, border: `1px ${r.near ? 'dashed' : 'solid'} ${Y.bd}`, marginBottom: 4, opacity: r.near || r.pr.expired ? 0.72 : 1 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <div style={{ flex: 1, minWidth: 0, fontSize: T.xs, fontWeight: 700, color: C.text, lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{p.name}</div>
                    <span style={{ flexShrink: 0, display: 'flex', gap: 4 }}>
                      {r.near && <Badge bg={C.orangeLt} color={C.orange} style={{ padding: '2px 7px' }}>확인 필요</Badge>}
                      {r.pr.always ? <Badge bg={C.bgAlt} color={C.sub} style={{ padding: '2px 7px' }}>{p.first_come ? '상시' : '기한 미정'}</Badge> : r.pr.recurNext ? <Badge bg={C.accentLt} color={C.accent} style={{ padding: '2px 7px' }}>매년</Badge> : <DDayBadge deadline={r.pr.end} />}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '3px 8px', marginTop: 3 }}>
                    <span style={{ flex: '1 1 160px', minWidth: 0, fontSize: T.xs, color: C.muted, lineHeight: 1.5 }}>
                      {/* 기관 · 금액 · 기간 — 항목 안에서는 줄이 안 끊기고(날짜 구간이 반으로 갈리지 않게) 가운뎃점에서만 줄바꿈 */}
                      {meta.map((m, i) => <React.Fragment key={i}><span style={{ whiteSpace: 'nowrap' }}>{m}</span>{' · '}</React.Fragment>)}
                      <span style={{ whiteSpace: 'nowrap', color: r.pr.expired ? C.red : C.sub }}>{r.pr.text}</span>
                      {r.pr.note && <>{' · '}<span style={{ whiteSpace: 'nowrap' }}>{r.pr.note}</span></>}
                    </span>
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 4, flexShrink: 0 }}>
                      {u ? (
                        <>
                          <a href={u} target="_blank" rel="noopener noreferrer" title={u} style={smallBtn}>공고문 보기 ↗</a>
                          <button type="button" onClick={() => void copy(p)} title="공고 주소를 복사합니다" style={smallBtn}>링크 복사</button>
                        </>
                      ) : <span style={{ ...smallBtn, cursor: 'default', color: C.muted, borderColor: C.border }}>링크 없음</span>}
                      {onWatch && <button type="button" onClick={() => onWatch(p)} title="이 사업 공고가 새로 나오면 화면 · 문자로 알려 드려요" style={smallBtn}>다음 공고 알림</button>}
                    </span>
                  </div>
                  {r.near && (p.missing || []).length > 0 && (
                    <div style={{ fontSize: T.xs, color: C.orange, marginTop: 3 }}>채우면 판정: {(p.missing || []).join(' · ')}</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* 꼬리 — 더 보기 · 접기 · 전체 목록 */}
          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
            {!filtering && hidden > 0 && (
              <button type="button" onClick={() => setExpanded(true)} style={{ flex: 1, padding: '4px 0', borderRadius: R.sm, border: `1px solid ${Y.bd}`, background: C.white, fontSize: T.xs, color: Y.fg, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>{hidden}건 더 보기</button>
            )}
            {!filtering && expanded && shown.length > limit && (
              <button type="button" onClick={() => setExpanded(false)} style={{ flex: 1, padding: '4px 0', borderRadius: R.sm, border: `1px solid ${Y.bd}`, background: C.white, fontSize: T.xs, color: Y.fg, cursor: 'pointer', fontFamily: 'inherit' }}>접기</button>
            )}
            {onNavigateGov && (
              <button type="button" onClick={onNavigateGov} style={{ flex: 1, padding: '4px 0', borderRadius: R.sm, border: `1px solid ${Y.bd}`, background: 'transparent', fontSize: T.xs, color: Y.fg, cursor: 'pointer', fontFamily: 'inherit' }}>전체 정부지원사업 보기 →</button>
            )}
          </div>
        </>
      )}
      {!loading && !err && total === 0 && onNavigateGov && (
        <button type="button" onClick={onNavigateGov} style={{ width: '100%', padding: '4px 0', borderRadius: R.sm, border: `1px solid ${Y.bd}`, background: 'transparent', fontSize: T.xs, color: Y.fg, cursor: 'pointer', fontFamily: 'inherit', marginTop: 4 }}>전체 정부지원사업 보기 →</button>
      )}
    </div>
  );
}
